import React from "react";

import {
  CreateMenuPopover,
  FavoritesSection,
  MyWorkflowsSection,
  ModuleMenu,
  type ModuleMenuSection
} from "#app/components/navigation/module-menu";
import { useIssueView } from "#app/components/issue-view";
import { useAppData } from "#app/lib/data";
import { useFavoriteIssues } from "#app/lib/hooks/use-favorite-issues";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useCanCreateProject } from "#app/lib/hooks/use-can-create-project";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";
import { WorkflowSettingsMenuPopup } from "#app/components/workflows/workflow-settings-menu";

// The module's primary list renders FLAT — the pane title is the group label
// (the reference module-menu grammar), so no "Workflows" section header and no
// collapse. Module-scope so the section list holds a stable component type
// across renders.
function FlatWorkflowsSection() {
  return <MyWorkflowsSection grouped={false} />;
}

export function WorkflowsModuleMenu() {
  const { workflows } = useAppData();
  // Mirror-backed rows, so the Favorites slot is decided on the shell's first
  // frame rather than popping in after the preferences fetch.
  const { sidebarRows: favoriteIssues } = useFavoriteIssues();
  const { openCreateIssue } = useIssueView();
  const canCreateWorkflow = usePermission("create.workflows");
  // Post-create destination: the settings editor is gated on
  // manage.edit_workflows, so a create-only user goes to the board instead.
  const canCreateProject = useCanCreateProject();
  const closeSidebar = useCloseSidebarIfMobile();
  const { openCreateWorkflow, openNewDashboard } = useNavDialogActions();

  // NOTE: order/inclusion here is the instance default — future user
  // preferences will reorder/filter these by section id. The All Workflows
  // roster page keeps no nav row: it's reached through the title menu's
  // "Manage Workflows". The flat workflow list leads; Favorites (and
  // an eventual Recent) sit below it.
  const sections = React.useMemo<ModuleMenuSection[]>(() => {
    const list: ModuleMenuSection[] = [];
    // Skip the workflow list when there's nothing to show or do — no visible
    // workflows AND no create permission.
    if (workflows.length > 0 || canCreateWorkflow) {
      list.push({ id: "my-workflows", Component: FlatWorkflowsSection });
    }
    if (favoriteIssues.length > 0) {
      list.push({ id: "favorites", Component: FavoritesSection });
    }
    return list;
  }, [favoriteIssues.length, workflows.length, canCreateWorkflow]);

  const onCreateProject = React.useMemo(
    () =>
      canCreateProject
        ? () => {
            closeSidebar();
            openCreateIssue({ source: "workflows_menu" });
          }
        : undefined,
    [canCreateProject, closeSidebar, openCreateIssue],
  );
  const onCreateWorkflow = React.useMemo(
    () =>
      canCreateWorkflow
        ? () => {
            closeSidebar();
            openCreateWorkflow();
          }
        : undefined,
    [canCreateWorkflow, closeSidebar, openCreateWorkflow],
  );
  // Same create catalog as Home.
  const onCreateDashboard = React.useCallback(() => {
    closeSidebar();
    openNewDashboard();
  }, [closeSidebar, openNewDashboard]);

  return (
    <>
      <ModuleMenu
        title="Workflows"
        sections={sections}
        titleMenu={<WorkflowSettingsMenuPopup />}
        createMenu={
          <CreateMenuPopover
            ariaLabel="Workflows actions"
            onCreateProject={onCreateProject}
            onCreateWorkflow={onCreateWorkflow}
            onCreateDashboard={onCreateDashboard}
          />
        }
      />
    </>
  );
}